import { BatchFaceSheets } from '../components/BatchFaceSheets';
import { CertEndingBadge, useCertEndingSoon } from '../components/CertEndingBadge';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import InsuranceFormModal from '../components/InsuranceFormModal';
import AddPatientModal from '../components/modals/AddPatientModal';
import FilterModal from '../components/modals/FilterModal';
import PatientPackagesModal from '../components/PatientPackagesModal';
import { AllergyBadges } from '../components/MedicationExtras';
import { Plus, Search, Edit, User, X, Upload, Trash2, Camera, RefreshCw, Filter, ChevronLeft, ChevronRight, FileText, Shield, ChevronDown, ChevronUp, FolderOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INSURANCE_OPTIONS = [
  'All', 'CIGNA', 'Medicare', 'Medicaid', 'Blue Cross Blue Shield',
  'Aetna', 'United Healthcare', 'Humana', 'Kaiser Permanente',
  'Anthem', 'Tricare', 'Molina Healthcare', 'WellCare'
];

const STATUS_OPTIONS = ['All', 'Active', 'Inactive', 'Discharged', 'Pending'];

// Patient Modal Component
const PatientModal = ({ selectedPatient, setShowModal, fileInputRef, uploading, handleProfilePictureUpload, handleRemoveProfilePicture, onViewChart, onEditInsurance }) => {
  if (!selectedPatient) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-testid="patient-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b bg-[#3498db] text-white rounded-t-lg">
          <h2 className="text-xl font-semibold">Patient Profile</h2>
          <button onClick={() => setShowModal(false)} className="p-1 hover:bg-white/20 rounded" data-testid="close-modal-btn">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6">
          <div className="flex flex-col items-center mb-6">
            <div className="relative group">
              {selectedPatient?.profilePicture ? (
                <img src={selectedPatient.profilePicture} alt={`${selectedPatient.firstName} ${selectedPatient.lastName}`}
                  className="w-32 h-32 rounded-full object-cover border-4 border-[#3498db] shadow-lg" />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-[#3498db] shadow-lg">
                  <User className="w-16 h-16 text-gray-400" />
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={() => fileInputRef.current?.click()}>
                {uploading ? <RefreshCw className="w-8 h-8 text-white animate-spin" /> : <Camera className="w-8 h-8 text-white" />}
              </div>
            </div>
            <input type="file" ref={fileInputRef} onChange={handleProfilePictureUpload} accept="image/*" className="hidden" />
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                <Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading...' : 'Upload Photo'}
              </Button>
              {selectedPatient?.profilePicture && (
                <Button variant="outline" size="sm" onClick={handleRemoveProfilePicture} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4 mr-2" />Remove
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-gray-600 text-sm">First Name</Label><p className="font-medium text-gray-900">{selectedPatient?.firstName}</p></div>
            <div><Label className="text-gray-600 text-sm">Last Name</Label><p className="font-medium text-gray-900">{selectedPatient?.lastName}</p></div>
            <div><Label className="text-gray-600 text-sm">Middle Name</Label><p className="font-medium text-gray-900">{selectedPatient?.middleName || '-'}</p></div>
            <div><Label className="text-gray-600 text-sm">DOB</Label><p className="font-medium text-gray-900">{selectedPatient?.dateOfBirth ? new Date(selectedPatient.dateOfBirth).toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: 'numeric'}) : '-'}</p></div>
            <div><Label className="text-gray-600 text-sm">Phone</Label><p className="font-medium text-gray-900">{selectedPatient?.phone || '-'}</p></div>
            <div><Label className="text-gray-600 text-sm">Status</Label><span className={`inline-flex px-2 py-1 rounded text-sm font-medium ${selectedPatient?.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{selectedPatient?.status || 'Active'}</span></div>
            <div><Label className="text-gray-600 text-sm">Insurance</Label><p className="font-medium text-gray-900">{selectedPatient?.insurance || '-'}</p></div>
            <div><Label className="text-gray-600 text-sm">Physician</Label><p className="font-medium text-gray-900">{selectedPatient?.physician || '-'}</p></div>
            <div><Label className="text-gray-600 text-sm">Primary Diagnosis</Label><p className="font-medium text-gray-900">{selectedPatient?.primaryDiagnosis || '-'}</p></div>
            <div><Label className="text-gray-600 text-sm">PAN #</Label><p className="font-medium text-gray-900">{selectedPatient?.pan || '-'}</p></div>
            <div className="col-span-2"><Label className="text-gray-600 text-sm">Address</Label><p className="font-medium text-gray-900">{selectedPatient?.address ? `${selectedPatient.address}, ${selectedPatient.city}, ${selectedPatient.state} ${selectedPatient.zip}` : '-'}</p></div>
          </div>
          <div className="mt-6 pt-4 border-t flex justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onViewChart(selectedPatient)} className="text-[#3498db]">
                <FileText className="w-4 h-4 mr-2" />View Chart
              </Button>
              <Button variant="outline" onClick={() => onEditInsurance(selectedPatient)} className="text-orange-500">
                <Shield className="w-4 h-4 mr-2" />Edit Insurance
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Close</Button>
              <Button className="bg-[#3498db] hover:bg-[#2980b9]"><Edit className="w-4 h-4 mr-2" />Edit Patient</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Advanced Filter Panel
const FilterPanel = ({ filters, setFilters, onClear, insuranceOptions }) => {
  return (
    <div className="bg-gray-50 border rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-700">Advanced Filters</h3>
        <Button variant="ghost" size="sm" onClick={onClear} className="text-gray-500">Clear All</Button>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <div>
          <Label className="text-sm text-gray-600">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm text-gray-600">Insurance</Label>
          <Select value={filters.insurance} onValueChange={(v) => setFilters({ ...filters, insurance: v })}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="All Insurance" /></SelectTrigger>
            <SelectContent>
              {insuranceOptions.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm text-gray-600">Physician</Label>
          <input
            type="text"
            value={filters.physician}
            onChange={(e) => setFilters({ ...filters, physician: e.target.value })}
            placeholder="Filter by physician"
            className="w-full mt-1 border rounded px-3 py-2 text-sm"
          />
        </div>
        <div>
          <Label className="text-sm text-gray-600">Diagnosis</Label>
          <input
            type="text"
            value={filters.diagnosis}
            onChange={(e) => setFilters({ ...filters, diagnosis: e.target.value })}
            placeholder="Filter by diagnosis"
            className="w-full mt-1 border rounded px-3 py-2 text-sm"
          />
        </div>
      </div>
    </div>
  );
};

const PatientDemographics = () => {
  const navigate = useNavigate();
  const [chartParams] = useSearchParams();
  useEffect(() => {
    const pid = chartParams.get('patientId');
    if (pid) navigate(`/dashboard/patients/demographics/${pid}`, { replace: true });
  }, [chartParams, navigate]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEntries, setShowEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [picked, setPicked] = useState({});
  const certEnding = useCertEndingSoon(5);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('lastName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filters, setFilters] = useState({
    status: 'All',
    insurance: 'All',
    physician: '',
    diagnosis: ''
  });
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [newInsurance, setNewInsurance] = useState('');
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const [packagesPatient, setPackagesPatient] = useState(null);
  const fileInputRef = useRef(null);

  // Get unique insurance providers from patients
  const [insuranceProviders, setInsuranceProviders] = useState(INSURANCE_OPTIONS);

  const [allergySummary, setAllergySummary] = useState({});
  useEffect(() => {
    fetchPatients();
    api.get('/allergies/summary').then(r => setAllergySummary(r.data.summary || {})).catch(() => {});
  }, []);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const response = await api.get('/patients');
      if (response.data.success) {
        setPatients(response.data.patients);
        // Get unique insurance providers
        const uniqueInsurance = ['All', ...new Set(response.data.patients.map(p => p.insurance).filter(Boolean))];
        setInsuranceProviders(uniqueInsurance);
      }
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setShowModal(true);
  };

  const handleViewDemographicsDetail = (patient) => {
    navigate(`/dashboard/patients/demographics/${patient.id}`);
  };

  const handleViewChart = (patient) => {
    setShowModal(false);
    navigate(`/dashboard/patient/chart?patientId=${patient.id}`);
  };

  const handleEditInsurance = (patient) => {
    setEditingPatient(patient);
    setNewInsurance(patient.insurance || '');
    setShowModal(false);
    setShowInsuranceModal(true);
  };

  const handleViewPackages = (patient) => {
    setPackagesPatient(patient);
    setShowPackagesModal(true);
  };

  const handleSaveInsurance = async () => {
    if (!editingPatient) return;
    try {
      const response = await api.put(`/patients/${editingPatient.id}`, {
        ...editingPatient,
        insurance: newInsurance
      });
      if (response.data.success) {
        toast.success('Insurance updated successfully');
        setPatients(patients.map(p => p.id === editingPatient.id ? { ...p, insurance: newInsurance } : p));
        setShowInsuranceModal(false);
      }
    } catch (error) {
      toast.error('Failed to update insurance');
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image size must be less than 5MB'); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        const token = localStorage.getItem('ivory_user') ? JSON.parse(localStorage.getItem('ivory_user')).token : localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/patients/${selectedPatient.id}/profile-picture`, {
          method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ profilePicture: base64Image })
        });
        const data = await response.json();
        if (data.success) {
          toast.success('Profile picture updated');
          setSelectedPatient({ ...selectedPatient, profilePicture: base64Image });
          setPatients(patients.map(p => p.id === selectedPatient.id ? { ...p, profilePicture: base64Image } : p));
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload profile picture');
      setUploading(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    try {
      const token = localStorage.getItem('ivory_user') ? JSON.parse(localStorage.getItem('ivory_user')).token : localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients/${selectedPatient.id}/profile-picture`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Profile picture removed');
        setSelectedPatient({ ...selectedPatient, profilePicture: null });
        setPatients(patients.map(p => p.id === selectedPatient.id ? { ...p, profilePicture: null } : p));
      }
    } catch (error) {
      toast.error('Failed to remove profile picture');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const clearFilters = () => {
    setFilters({ status: 'All', insurance: 'All', physician: '', diagnosis: '' });
    setSearchTerm('');
  };

  // Filter and sort patients
  const filteredPatients = patients
    .filter(p => {
      const matchesSearch = 
        p.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.middleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.phone?.includes(searchTerm) ||
        p.insurance?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filters.status === 'All' || p.status === filters.status;
      const matchesInsurance = filters.insurance === 'All' || p.insurance === filters.insurance;
      const matchesPhysician = !filters.physician || p.physician?.toLowerCase().includes(filters.physician.toLowerCase());
      const matchesDiagnosis = !filters.diagnosis || p.primaryDiagnosis?.toLowerCase().includes(filters.diagnosis.toLowerCase());
      return matchesSearch && matchesStatus && matchesInsurance && matchesPhysician && matchesDiagnosis;
    })
    .sort((a, b) => {
      let comparison = 0;
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      comparison = aVal.toString().localeCompare(bVal.toString());
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Pagination
  const totalPages = Math.ceil(filteredPatients.length / showEntries);
  const startIndex = (currentPage - 1) * showEntries;
  const paginatedPatients = filteredPatients.slice(startIndex, startIndex + showEntries);

  const formatDOB = (dob) => {
    if (!dob) return '';
    const date = new Date(dob);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header - Alora Plus Style */}
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-light text-[#3498db]">Patient Demographics</h1>
          <span className="text-gray-400">&gt;</span>
          <span className="text-gray-500">Patient Search</span>
        </div>

        {/* Title with Add Button */}
        <div className="flex items-center gap-3 mt-6">
          <User className="w-6 h-6 text-gray-400" />
          <h2 className="text-xl text-gray-600">Patient Demographics</h2>
          <Button 
            className="bg-[#3498db] hover:bg-[#2980b9] text-white px-4 py-1 text-sm rounded" 
            data-testid="add-patient-btn"
            onClick={() => setShowAddPatientModal(true)}
          >
            <Plus className="w-4 h-4 mr-1" />Add
          </Button>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Search:</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Name, phone, insurance..."
              className="border border-gray-300 rounded px-3 py-1.5 w-64 focus:outline-none focus:border-[#3498db]"
              data-testid="list-patient-search-input"
            />
            <Button
              variant="outline"
              className="border-[#3498db] text-[#3498db] hover:bg-[#3498db] hover:text-white"
              onClick={() => setShowFilterModal(true)}
              data-testid="filter-btn"
            >
              <Filter className="w-4 h-4 mr-1" />
              Advanced Filters
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <BatchFaceSheets patients={patients.filter(p => picked[p.id])} onDone={() => setPicked({})} />
            <span className="text-sm text-gray-500">
              {filteredPatients.length} patients found
            </span>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Show</span>
              <select
                value={showEntries}
                onChange={(e) => { setShowEntries(Number(e.target.value)); setCurrentPage(1); }}
                className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-[#3498db]"
                data-testid="show-entries-select"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-gray-600">entries</span>
            </div>
          </div>
        </div>

        {/* Advanced Filter Panel */}
        {showFilters && (
          <FilterPanel
            filters={filters}
            setFilters={(f) => { setFilters(f); setCurrentPage(1); }}
            onClear={clearFilters}
            insuranceOptions={insuranceProviders}
          />
        )}

        {/* Patient Table */}
        <div className="bg-white rounded border border-gray-200 mt-4 overflow-hidden">
          <table className="w-full" data-testid="patients-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-500">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" data-testid="select-all-patients"
                      checked={paginatedPatients.length > 0 && paginatedPatients.every(p => picked[p.id])}
                      onChange={(e) => setPicked(prev => ({ ...prev, ...Object.fromEntries(paginatedPatients.map(p => [p.id, e.target.checked])) }))} />
                    Select
                  </label>
                </th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:text-[#3498db]" onClick={() => handleSort('lastName')}>
                  Last Name <SortIcon field="lastName" />
                </th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:text-[#3498db]" onClick={() => handleSort('firstName')}>
                  First Name <SortIcon field="firstName" />
                </th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-500">Middle Name</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:text-[#3498db]" onClick={() => handleSort('dateOfBirth')}>
                  DOB <SortIcon field="dateOfBirth" />
                </th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:text-[#3498db]" onClick={() => handleSort('insurance')}>
                  Insurance <SortIcon field="insurance" />
                </th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:text-[#3498db]" onClick={() => handleSort('status')}>
                  Status <SortIcon field="status" />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : paginatedPatients.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No patients found</td></tr>
              ) : (
                paginatedPatients.map((patient, index) => (
                  <tr key={patient.id} className={`border-b border-gray-100 hover:bg-blue-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                      onClick={() => handleViewDemographicsDetail(patient)}>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <input type="checkbox" className="mr-1" data-testid={`select-patient-${patient.id}`}
                          checked={Boolean(picked[patient.id])} onChange={(e) => setPicked(prev => ({ ...prev, [patient.id]: e.target.checked }))} />
                        <button onClick={() => handleViewDemographicsDetail(patient)} className="text-[#3498db] hover:text-[#2980b9]" title="Edit Demographics" data-testid={`edit-patient-${patient.id}`}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleViewChart(patient)} className="text-gray-500 hover:text-[#3498db]" title="View Chart">
                          <FileText className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEditInsurance(patient)} className="text-gray-500 hover:text-orange-500" title="Edit Insurance">
                          <Shield className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleViewPackages(patient)} className="text-gray-500 hover:text-green-600" title="Application Packages" data-testid={`packages-${patient.id}`}>
                          <FolderOpen className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-900 font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        {patient.lastName}
                        <AllergyBadges allergies={allergySummary[patient.id]} patientId={patient.id} />
                        <CertEndingBadge info={certEnding[patient.id]} patientId={patient.id} />
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-700">{patient.firstName}</td>
                    <td className="px-3 py-3 text-gray-700">{patient.middleName || ''}</td>
                    <td className="px-3 py-3 text-gray-700">{formatDOB(patient.dateOfBirth)}</td>
                    <td className="px-3 py-3 text-gray-700">{patient.insurance || '-'}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                        patient.status === 'Active' ? 'bg-green-100 text-green-800' :
                        patient.status === 'Discharged' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {patient.status || 'Active'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-600">
            Showing {filteredPatients.length > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + showEntries, filteredPatients.length)} of {filteredPatients.length} entries
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">First</button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">Previous</button>
            {getPageNumbers().map((page, idx) => (
              <button key={idx} onClick={() => typeof page === 'number' && setCurrentPage(page)} disabled={page === '...'}
                className={`px-3 py-1 border rounded text-sm ${currentPage === page ? 'bg-[#3498db] text-white border-[#3498db]' : page === '...' ? 'border-transparent' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >{page}</button>
            ))}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">Next</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">Last</button>
          </div>
        </div>
      </div>

      {/* Patient Modal */}
      {showModal && (
        <PatientModal
          selectedPatient={selectedPatient}
          setShowModal={setShowModal}
          fileInputRef={fileInputRef}
          uploading={uploading}
          handleProfilePictureUpload={handleProfilePictureUpload}
          handleRemoveProfilePicture={handleRemoveProfilePicture}
          onViewChart={handleViewChart}
          onEditInsurance={handleEditInsurance}
        />
      )}

      {/* Insurance Form Modal - Alora Plus Style */}
      {showInsuranceModal && editingPatient && (
        <InsuranceFormModal
          patient={editingPatient}
          onClose={() => setShowInsuranceModal(false)}
          onSave={(updatedPatient) => {
            setPatients(patients.map(p => 
              p.id === updatedPatient.id ? updatedPatient : p
            ));
          }}
        />
      )}

      {/* Add Patient Modal */}
      {showAddPatientModal && (
        <AddPatientModal
          onClose={() => setShowAddPatientModal(false)}
          onSave={(newPatient) => {
            setPatients([...patients, newPatient]);
            fetchPatients();
          }}
        />
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <FilterModal
          onClose={() => setShowFilterModal(false)}
          currentFilters={filters}
          onApply={(newFilters) => {
            setFilters({
              status: newFilters.status || 'All',
              insurance: newFilters.insurance || 'All',
              physician: '',
              diagnosis: ''
            });
            setCurrentPage(1);
          }}
        />
      )}

      {/* Patient Packages Modal */}
      <PatientPackagesModal
        patient={packagesPatient}
        isOpen={showPackagesModal}
        onClose={() => {
          setShowPackagesModal(false);
          setPackagesPatient(null);
        }}
      />
    </DashboardLayout>
  );
};

export default PatientDemographics;
